import { UTCDate } from '@date-fns/utc';
import { endOfDay, startOfDay } from 'date-fns';
import { useEffect, useMemo, useReducer } from 'react';
import { useGetApiPickemsCurrentPlayerBetable } from '@/api/backoffice/pickems/pickems';
import type { GetApiPickemsCurrentPlayerBetableParams } from '@/api/backoffice-schemas';
import type { Game } from '@/constants/game.const';
import { mapGameFilterToVideoGameName } from '@/utils/gameMappers';
import { isDailyPickem } from '@/utils/pickems';
import { pickemsReducer } from './pickems.reducer';

export const usePickems = (
  options:
    | {
        game: Game;
        date: UTCDate | Date;
      }
    | {
        tournamentId: string;
      }
) => {
  const [state, dispatch] = useReducer(pickemsReducer, { pickems: [] });

  const isTournamentPickems = 'tournamentId' in options;

  const pickemsQueryParams =
    useMemo((): GetApiPickemsCurrentPlayerBetableParams => {
      if (isTournamentPickems) {
        return {
          TournamentId: options.tournamentId,
        };
      }
      return {
        Game: mapGameFilterToVideoGameName(options.game),
        DateTimeFrom: startOfDay(new UTCDate(options.date)).toISOString(),
        DateTimeTo: endOfDay(new UTCDate(options.date)).toISOString(),
      };
    }, [options, isTournamentPickems]);

  const { data, isLoading } = useGetApiPickemsCurrentPlayerBetable(
    {
      ...pickemsQueryParams,
      PageSize: 100,
    },
    {
      query: {
        refetchOnMount: true,
        refetchOnWindowFocus: true,
      },
    }
  );

  // Filter out tournament pickems for daily pickems
  const betablePickems = useMemo(
    () =>
      isTournamentPickems
        ? data?.data.items
        : data?.data.items?.filter(isDailyPickem),
    [data, isTournamentPickems]
  );

  useEffect(() => {
    if (betablePickems) {
      dispatch({
        type: 'SET_PICKEMS',
        payload: betablePickems,
      });
    }
  }, [betablePickems]);

  const handleSelect = (pickemId: string, winnerId: string) => {
    dispatch({ type: 'SELECT_WINNER', payload: { pickemId, winnerId } });
  };

  const handleClearAll = () => {
    dispatch({ type: 'CLEAR_ALL' });
  };

  const selectedPickems = state.pickems.filter((x) => x.selectedWinnerId);

  return {
    pickems: betablePickems ?? [],
    selectedPickems,
    isLoading,
    handleSelect,
    handleClearAll,
    totalPickems: state.pickems.length,
  };
};
